import {Component, Input, OnInit} from '@angular/core';
import {AbstractControl, FormControl, Validators} from '@angular/forms';
import {merge} from 'rxjs';
import {take} from 'rxjs/operators';
import {ConfigAPIService} from '@app/services/api/config-api.service';

@Component({
  selector: 'app-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.scss'],
  standalone: false
})
export class PhoneNumberInputComponent implements OnInit {

  @Input() control: AbstractControl;
  @Input() defaultPrefix: string = '';
  @Input() splitPrefix = ')';
  @Input() readonly = false;

  defaultPhonePrefixes: string[] = [
    '386', // Slovenia
    '43',  // Austria
    '39',  // Italy
    '385', // Croatia
    '36',  // Hungary
    '49',  // Germany
    '421', // Slovakia
    '381', // Serbia
    '387', // Bosnia and Herzegovina
    '382', // Montenegro
    '389', // North Macedonia
    '355', // Albania
    '383', // Kosovo
    '48',  // Poland
    '420', // Czech Republic
    '40',  // Romania
    '359', // Bulgaria
    '30',  // Greece
    '31',  // Netherlands
    '32',  // Belgium
    '34',  // Spain
    '33',  // France
    '351', // Portugal
    '41',  // Switzerland
    '46',  // Sweden
    '45',  // Denmark
    '358', // Finland
    '353', // Ireland
    '47',  // Norway
    '370', // Lithuania
    '371', // Latvia
    '372', // Estonia
    '356', // Malta
    '357', // Cyprus
    '354', // Iceland
    '376', // Andorra
    '375', // Belarus
    '7',   // Russia
    '90'   // Turkey
  ];

  @Input() prefixes: Array<string> = this.defaultPhonePrefixes;

  @Input() label = '';
  @Input() placeholder = '';
  @Input() maxLength = 256;
  @Input() minLength = 0;
  @Input() append = '';
  readonly maxNationalDigits = 13;
  mask = "00-000-00000000";
  maskRegex = /^\d{2}-\d{3}-\d{3,8}$/;

  prefix = new FormControl(this.defaultPrefix, [Validators.required]);
  number = new FormControl('', [Validators.required, Validators.pattern(this.maskRegex)]);


  constructor(private configApi: ConfigAPIService) {
  }

  ngOnInit(): void {
    if (this.prefixes.length > 0) {
      this.prefix.setValue(this.defaultPrefix || this.prefixes[0]);

      if (!this.defaultPrefix) {
        this.configApi.getAccountConfig().pipe(take(1)).subscribe((config) => {
          const dialCode = config?.dialCode;

          if (!dialCode || this.control.value) {
            return;
          }

          if (!this.prefixes.includes(dialCode)) {
            // A caller-supplied list is a deliberate restriction — don't widen it.
            if (this.prefixes !== this.defaultPhonePrefixes) {
              return;
            }
            this.prefixes = [dialCode, ...this.prefixes];
          }

          this.prefix.setValue(dialCode);
        });
      }
    }

    this.onControlValueChange(this.control.value);

    this.control.valueChanges.subscribe(value => {
      this.onControlValueChange(value);
    });

    const inputs = merge(
      this.prefix.valueChanges,
      this.number.valueChanges
    );

    inputs.subscribe(() => {
      let rawNumber = this.unmaskPhone(this.number.value || '').slice(0, this.maxNationalDigits);
      const maskedNumber = this.maskPhone(rawNumber);

      this.number.setValue(maskedNumber, {emitEvent: false});

      if (this.maskRegex.test(maskedNumber) && maskedNumber.length > 0) {
        this.control.setValue(`(${this.prefix.value})${rawNumber}`);
      } else {
        this.control.setValue(null);
      }
    });

    this.number.valueChanges.subscribe(val => {
      const raw = this.unmaskPhone(val || '');
      if (raw.length > this.maxNationalDigits) {
        const trimmed = raw.slice(0, this.maxNationalDigits);
        const masked = this.maskPhone(trimmed);
        this.number.setValue(masked, {emitEvent: false});
      }
    });
  }

  private onControlValueChange(value) {
    if (value) {
      // Check if value is in new format: (PREFIX)XXXXXXXX
      if (value.includes(this.splitPrefix)) {
        const splitValue = value.split(this.splitPrefix);
        let prefix = splitValue[0].replace('(', '').trim();
        let splicedNumber = splitValue[1];

        if (prefix && this.prefixes.includes(prefix)) {
          this.prefix.setValue(prefix, {emitEvent: false});
        }

        if (splicedNumber) {
          const rawNumber = splicedNumber.replace(/\D/g, '').slice(0, this.maxNationalDigits);
          this.number.setValue(this.maskPhone(rawNumber), {emitEvent: false});
          this.control.setValue(`(${this.prefix.value})${rawNumber}`, {emitEvent: false});
        }
      } else {
        // Handle legacy plain text format (e.g., "38632555455" or "040123456")
        const cleanNumber = value.replace(/\D/g, '');

        // Try to detect prefix in legacy format
        let detectedPrefix = '';
        let phoneNumber = cleanNumber;

        // Check for prefixes starting with longest first (e.g., 386 before 38)
        const sortedPrefixes = [...this.prefixes].sort((a, b) => b.length - a.length);
        for (const prefix of sortedPrefixes) {
          if (cleanNumber.startsWith(prefix)) {
            detectedPrefix = prefix;
            phoneNumber = cleanNumber.substring(prefix.length);
            break;
          }
        }

        if (detectedPrefix) {
          this.prefix.setValue(detectedPrefix, {emitEvent: false});
        }

        if (phoneNumber.length >= 8) {
          const rawNumber = phoneNumber.slice(0, this.maxNationalDigits);
          const masked = this.maskPhone(rawNumber);
          this.number.setValue(masked, {emitEvent: false});

          // Convert legacy format to new format and update control
          const newFormat = `(${detectedPrefix || this.prefix.value})${rawNumber}`;
          this.control.setValue(newFormat, {emitEvent: false});
        }
      }
    }
  }

  private maskPhone(value: string): string {
    const v = value.replace(/\D/g, '').slice(0, this.maxNationalDigits);
    if (v.length > 5) return `${v.slice(0, 2)}-${v.slice(2, 5)}-${v.slice(5)}`;
    if (v.length > 2) return `${v.slice(0, 2)}-${v.slice(2, 5)}`;
    return v;
  }

  private unmaskPhone(value: string): string {
    return (value || '').replace(/\D/g, '');
  }
}
